import { supabase, SUPABASE_URL } from '../supabaseClient';
import { IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import {
  USER_PROFILE_IMAGE_META_PATH_KEY,
  USER_PROFILE_IMAGE_META_VERSION_KEY,
} from '../utils/userProfileImage';
import { notifySlideImageStorageChanged, type SlideImageStorageUsage } from './slideImageStorageService';

const BUCKET = 'user-profile-images';
const CACHE_CONTROL = '31536000';

function genStorageFileName(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${IMAGE_WEBP_EXT}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${IMAGE_WEBP_EXT}`;
}

export function publicUrlForUserProfileImagePath(storagePath: string): string {
  const path = storagePath.trim();
  if (!path || !SUPABASE_URL) return '';
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data.publicUrl || '';
}

export async function fetchUserProfileImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
    bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? 104857600),
  };
}

async function assertQuota(blobSize: number): Promise<void> {
  const usage = await fetchUserProfileImageStorageUsage();
  if (usage && usage.bytesUsed + blobSize > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }
}

export async function uploadUserProfileImage(blob: Blob): Promise<{
  userId: string;
  path: string;
  url: string;
  version: number;
}> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  await assertQuota(blob.size);

  const prevPath =
    typeof userData.user?.user_metadata?.[USER_PROFILE_IMAGE_META_PATH_KEY] === 'string'
      ? String(userData.user.user_metadata[USER_PROFILE_IMAGE_META_PATH_KEY]).trim()
      : '';

  const fileName = genStorageFileName();
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_user_profile_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const version = Date.now();
  const { error: metaErr } = await supabase.auth.updateUser({
    data: {
      [USER_PROFILE_IMAGE_META_PATH_KEY]: storagePath,
      [USER_PROFILE_IMAGE_META_VERSION_KEY]: version,
    },
  });
  if (metaErr) {
    try {
      await supabase.rpc('release_user_profile_image_asset', { p_path: storagePath });
    } catch {
      /* ignore */
    }
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw metaErr;
  }

  if (prevPath && prevPath !== storagePath) {
    try {
      await supabase.storage.from(BUCKET).remove([prevPath]);
    } catch {
      /* 이전 파일 정리 실패는 무시 */
    }
  }

  const url = publicUrlForUserProfileImagePath(storagePath);
  if (!url) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { userId, path: storagePath, url, version };
}

export async function deleteUserProfileImage(storagePath: string): Promise<void> {
  const path = storagePath.trim();
  if (!path) return;

  try {
    await supabase.rpc('release_user_profile_image_asset', { p_path: path });
  } catch {
    /* 메타 없으면 무시 */
  }

  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error && !/not.?found|Object not found/i.test(error.message ?? '')) {
    throw error;
  }

  const { error: metaErr } = await supabase.auth.updateUser({
    data: {
      [USER_PROFILE_IMAGE_META_PATH_KEY]: '',
      [USER_PROFILE_IMAGE_META_VERSION_KEY]: 0,
    },
  });
  if (metaErr) throw metaErr;

  notifySlideImageStorageChanged();
}
